import { Box, ButtonBase, Typography } from '@mui/material';
import React, { FC } from 'react';
import { Button } from 'reactstrap';
import { getCurrencySymbol } from 'src/lib/utils';
import { formatPricingAmount } from 'src/pages/subscription-new/utils/plans.util';
import colors from 'src/styles/equalution/colors';
import { ORDER_SUMMARY_LINE_ITEM_WIDTH } from '../../constants';

/**
 * Shared ActionButton component for order summary rows
 */
export const ActionButton: FC<{
  label: string;
  onClick: () => void;
  isLoading: boolean;
  color?: string;
  useOutlineButton?: boolean;
}> = ({
  label,
  isLoading,
  onClick,
  color = colors.SALAD,
  useOutlineButton = false,
}) => {
  if (useOutlineButton) {
    return (
      <Button size="sm" outline disabled={isLoading} onClick={onClick}>
        {label}
      </Button>
    );
  }
  return (
    <ButtonBase disabled={isLoading} onClick={onClick}>
      <Typography
        variant="button"
        sx={{
          fontSize: '14px',
          textTransform: 'unset',
          textDecoration: 'underline',
          cursor: 'pointer',
          color: color,
          fontWeight: 600,
          textAlign: 'right',
        }}>
        {label}
      </Typography>
    </ButtonBase>
  );
};

/**
 * Shared TotalRow component for order summaries
 */
export const TotalRow: FC<{ total: number; billingLabel?: string }> = ({
  total,
  billingLabel = 'billed today',
}) => (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
    <Typography
      sx={{
        fontSize: '20px',
        color: colors.OFF_BLACK,
        fontWeight: 700,
        width: ORDER_SUMMARY_LINE_ITEM_WIDTH,
        flex: 'none',
        paddingRight: '16px',
      }}>
      Total
    </Typography>

    <Box
      sx={{
        display: 'flex',
        flex: 1,
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: colors.OFF_BLACK,
            whiteSpace: 'nowrap',
          }}>
          {getCurrencySymbol() + formatPricingAmount(total)}
        </Typography>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 400,
            color: colors.OFF_BLACK,
            marginTop: '-2px',
            whiteSpace: 'nowrap',
          }}>
          {billingLabel}
        </Typography>
      </Box>
    </Box>
  </Box>
);

/**
 * Generic AddonRow component for displaying addons in order summary
 */
export const AddonRow: FC<{
  label: string;
  price: number;
  priceFormat?: string; // e.g., "/month" or empty string
  onRemove: () => void;
  isLoading: boolean;
  removeButtonColor?: string;
  /** When provided, shows strikethrough next to the current price (e.g. $59 line through, $0 emphasised). */
  originalPrice?: number;
  /** Hide the "Remove" action — used for non-removable items like gift bundles. */
  hideRemove?: boolean;
}> = ({
  label,
  price,
  priceFormat = '',
  onRemove,
  isLoading,
  removeButtonColor = colors.ERROR_RED,
  originalPrice,
  hideRemove = false,
}) => {
  const showOriginal =
    originalPrice !== undefined && Number(originalPrice) !== Number(price);
  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
      <Typography
        sx={{
          fontSize: '16px',
          color: colors.OFF_BLACK,
          fontWeight: 400,
          width: ORDER_SUMMARY_LINE_ITEM_WIDTH,
          flex: 'none',
          paddingRight: '16px',
        }}>
        {label}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
            {showOriginal && (
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: colors.OFF_BLACK,
                  textDecoration: 'line-through',
                  whiteSpace: 'nowrap',
                }}>
                {getCurrencySymbol() + Number(originalPrice).toFixed(0)}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                color: colors.OFF_BLACK,
                whiteSpace: 'nowrap',
              }}>
              {getCurrencySymbol() + Number(price).toFixed(0)}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: colors.OFF_BLACK,
              marginTop: '-2px',
              whiteSpace: 'nowrap',
            }}>
            {priceFormat && ` ${priceFormat}`}
          </Typography>
        </Box>
        {!hideRemove && (
          <ActionButton
            isLoading={isLoading}
            label="Remove"
            onClick={onRemove}
            color={removeButtonColor}
          />
        )}
      </Box>
    </Box>
  );
};
